import {
  signUp as amplifySignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { registerPlugin } from '@capacitor/core';
import { isIOS, isNative } from './platform';
import { postAppleNative } from './api';

const AppleSignIn = registerPlugin('AppleSignIn');

const COGNITO_CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';

function injectCognitoTokens({ idToken, accessToken, refreshToken, username }) {
  const prefix = `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}`;
  if (!username) {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      username = payload['cognito:username'] || payload.email || payload.sub;
    } catch {}
  }
  if (!username) throw new Error('Could not determine Cognito username from idToken');
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, idToken);
  localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken);
  localStorage.setItem(`${prefix}.${username}.refreshToken`, refreshToken);
  localStorage.setItem(`${prefix}.${username}.clockDrift`, '0');
}

async function nativeAppleSignIn() {
  const result = await AppleSignIn.signIn();
  const fullName = [result.fullName?.givenName, result.fullName?.familyName].filter(Boolean).join(' ');
  const tokens = await postAppleNative({
    identityToken: result.identityToken,
    fullName: fullName || undefined,
  });
  injectCognitoTokens(tokens);
  // Force Amplify to re-read the session from localStorage on next call.
  return tokens;
}

export async function signInWithApple() {
  if (isNative() && isIOS()) {
    return nativeAppleSignIn();
  }
  return signInWithRedirect({ provider: { custom: 'SignInWithApple' } });
}

export async function signUp(email, password) {
  return amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email } },
  });
}

export async function confirmSignUp(email, code) {
  const { confirmSignUp: confirm } = await import('aws-amplify/auth');
  return confirm({ username: email, confirmationCode: code });
}

export async function resendCode(email) {
  const { resendSignUpCode } = await import('aws-amplify/auth');
  return resendSignUpCode({ username: email });
}

export async function signIn(email, password) {
  return amplifySignIn({ username: email, password });
}

export async function signOut() {
  await amplifySignOut();
}

export async function getCurrentUser() {
  try {
    return await amplifyGetCurrentUser();
  } catch {
    return null;
  }
}

/**
 * Read the signed-in user directly from the Cognito tokens Amplify persists in
 * localStorage. Used as a fallback when `getCurrentUser()` hangs (Amplify can
 * block indefinitely offline) so a genuinely signed-in user is NOT silently
 * demoted to anonymous — which would hide their real habits. Returns a
 * getCurrentUser-shaped object, or null if no valid session is cached.
 */
export function getCachedUser() {
  try {
    if (typeof localStorage === 'undefined' || !COGNITO_CLIENT_ID) return null;
    const prefix = `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}`;
    const lastUser = localStorage.getItem(`${prefix}.LastAuthUser`);
    if (!lastUser) return null;
    const idToken = localStorage.getItem(`${prefix}.${lastUser}.idToken`);
    if (!idToken) return null;
    const payload = JSON.parse(
      atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    // Reject an expired token — treat as signed-out rather than surface stale data.
    if (payload.exp && Date.now() >= payload.exp * 1000) return null;
    const email = payload.email || payload['cognito:username'];
    return {
      userId: payload.sub,
      username: lastUser,
      signInDetails: email && String(email).includes('@') ? { loginId: email } : undefined,
    };
  } catch {
    return null;
  }
}

export async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}
